// components/Reports/ReportPdfTemplate.js
import React from "react";
import { useRfi } from "../../contexts/RfiContext";
import { PersonOutlined } from "@mui/icons-material";

const ReportPdfTemplate = ({
  rfiDetails,
  reportTitle = "INSPECTION REPORT",
  stageNumber = null, // Stage number (1, 2, 3, 4)
  isVisible = false, // Control visibility for testing
  templateId = "report-pdf-template", // Unique ID for this template instance
  children, // Stage-specific content to render
}) => {
  const workOrderData = {
    // Core WorkOrderForm fields (all 8 fields included)
    workOrderNumber:
      rfiDetails?.work_order_number || rfiDetails?.wo_number || "—",
    kdNumber: rfiDetails?.kd_number || "—",
    projectTitle: rfiDetails?.project_title || "—",
    typeOfAsset: rfiDetails?.type_of_asset || rfiDetails?.asset_type || "—",
    creationDate: rfiDetails?.creation_date
      ? new Date(rfiDetails.creation_date).toLocaleDateString()
      : "—",
    buildWorkshop: rfiDetails?.build_workshop || "—",
    model: rfiDetails?.rfi_model?.model_name || rfiDetails?.model_name || "—",
    purpose: rfiDetails?.purpose?.purpose_name || rfiDetails?.purpose || "—",

    // Additional fields from reference image
    customer: rfiDetails?.customer_name || rfiDetails?.client || "—",
    reportNo: rfiDetails?.rfi_id || "—",
    scopeJob: rfiDetails?.scope_job || "—",
    dateTested: rfiDetails?.creation_date
      ? new Date(rfiDetails.creation_date).toLocaleDateString()
      : "—",
    subject: rfiDetails?.purpose?.purpose_name || "—",
    standardUsed: rfiDetails?.standard_used || "—",
    serialNo: rfiDetails?.serial_no || "—",
    jobNo: rfiDetails?.job_no || "—",
    location: rfiDetails?.build_workshop || "—",
    assetType: rfiDetails?.type_of_asset || rfiDetails?.asset_type || "—",
  };

  const manager_name =
    rfiDetails?.qc_manager.first_name +
      " " +
      rfiDetails?.qc_manager.last_name || "—";

  const coordinator_name =
    rfiDetails?.qc_coordinator.first_name +
      " " +
      rfiDetails?.qc_coordinator.last_name || "—";

  // Get stage data from Context for remarks based on stageNumber
  // Array index = stageNumber - 1 (e.g., stage 3 → index 2)
  const { stagesData } = useRfi();
  const stagesReview = stagesData.stagesReview.data;
  const stageData = stageNumber ? stagesReview?.[stageNumber - 1] || {} : {};

  return (
    <div
      id={templateId}
      style={{
        position: isVisible ? "relative" : "fixed",
        left: isVisible ? "auto" : "-9999px",
        top: isVisible ? "auto" : 0,
        width: "210mm",
        // Only set minHeight for preview, let PDF use auto height
        minHeight: isVisible ? "297mm" : "auto",
        height: "auto",
        backgroundColor: "#ffffff",
        fontFamily: "'Arial', sans-serif",
        fontSize: "11px",
        lineHeight: "1.3",
        overflow: "visible",
        // Only set visibility/opacity for modal view, leave default for PDF generation
        ...(isVisible && { visibility: "visible", opacity: 1 }),
        pointerEvents: isVisible ? "auto" : "none",
        zIndex: isVisible ? 1 : -1000000,
      }}
    >
      {/* Page Container with Border */}
      <div
        style={{
          width: "100%",
          // Only set minHeight for preview, let PDF use auto height
          minHeight: isVisible ? "297mm" : "auto",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          // Use padding and background to create border effect for better PDF rendering
          padding: "3px",
          backgroundColor: "#073642", // Theme secondary color border
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            // Only set minHeight for preview, let PDF use auto height
            minHeight: isVisible ? "calc(297mm - 6px)" : "auto",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header Section - Appears on every page */}
          <div
            data-pdf-section="header"
            style={{
              background: "linear-gradient(135deg, #073642 0%, #0e4a57 100%)",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {/* Logo Column */}
            <div
              style={{
                flex: "0 0 110px",
                display: "flex",
                alignItems: "center",
                padding: "6px",
                backgroundColor: "#fff",
                borderRadius: "4px",
              }}
            >
              <img
                src="/austin-logo.png"
                alt="Austin Logo"
                style={{ width: "100px", height: "20px", objectFit: "contain" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>

            {/* Title Column with Main Heading and Stage Title */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              {/* Main Heading */}
              <div
                style={{
                  color: "#fff",
                  textAlign: "center",
                  padding: "5px 10px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  borderBottom: "2px solid rgba(255,255,255,0.3)",
                }}
              >
                DIMENSIONAL REPORT
              </div>

              {/* Stage-specific Title */}
              <div
                style={{
                  color: "#fff",
                  textAlign: "center",
                  padding: "4px 10px",
                  fontSize: "12px",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                }}
              >
                {reportTitle}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div
            data-pdf-section="common-content"
            style={{
              flex: 1,
              // padding: "8px",
            }}
          >
            {/* Work Order Form Section */}
            <div style={{ marginBottom: "12px" }}>
              {/* Work Order Number Header */}
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                  padding: "6px 8px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e0e0e0",
                  borderLeft: "4px solid #073642",
                  borderRadius: "2px",
                  color: "#073642",
                }}
              >
                Work Order Number: {workOrderData.workOrderNumber}
              </div>

              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <tbody>
                  {/* Row 1: Customer and KD Number */}
                  <tr>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        width: "20%",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      CUSTOMER
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        width: "30%",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {workOrderData.customer}
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        width: "20%",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      KD NUMBER
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        width: "30%",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {workOrderData.kdNumber}
                    </td>
                  </tr>

                  {/* Row 2: Project Title and Type of Asset */}
                  <tr>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        width: "20%",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      PROJECT TITLE
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        width: "30%",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {workOrderData.projectTitle}
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        width: "20%",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      TYPE OF ASSET
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        width: "30%",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {workOrderData.typeOfAsset}
                    </td>
                  </tr>

                  {/* Row 3: Creation Date and Build Workshop */}
                  <tr>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      CREATION DATE
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {workOrderData.creationDate}
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      BUILD WORKSHOP
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {workOrderData.buildWorkshop}
                    </td>
                  </tr>

                  {/* Row 4: Model, Purpose, and RFI Number */}
                  <tr>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      MODEL
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {workOrderData.model}
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      PURPOSE
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {workOrderData.purpose}
                    </td>
                  </tr>

                  {/* Row 5: RFI Number and Stage */}
                  <tr>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      RFI NUMBER
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {rfiDetails?.rfi_id || workOrderData.workOrderNumber}
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        fontWeight: "bold",
                        backgroundColor: "#073642",
                        color: "#fff",
                        fontSize: "9px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      STAGE
                    </td>
                    <td
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "5px 8px",
                        backgroundColor: "#fff",
                        color: "#222222",
                      }}
                    >
                      {stageNumber ? `${stageNumber}` : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* QC Remarks Section */}
            {(stageData?.coordinator_remarks || stageData?.manager_remarks) && (
              <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    marginBottom: "8px",
                    padding: "5px 8px",
                    backgroundColor: "#073642",
                    color: "#fff",
                    letterSpacing: "0.4px",
                  }}
                >
                  QC REMARKS
                </div>

                {/* Remarks Container - Horizontal Layout */}
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "stretch",
                  }}
                >
                  {/* QC Coordinator Remark */}
                  {stageData?.coordinator_remarks && (
                    <div
                      style={{
                        flex: 1,
                        padding: "6px 8px",
                        backgroundColor: "#f1f8f4",
                        border: "1px solid #4caf50",
                        borderLeft: "3px solid #4caf50",
                        borderRadius: "3px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "4px",
                          }}
                        >
                          <div style={{ margin: "6px" }}>
                            <PersonOutlined sx={{ fontSize: 14 }} />
                          </div>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: "bold",
                              color: "#388e3c",
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                            }}
                          >
                            <div>QC Coordinator</div>
                            <div style={{ color: "black", fontSize: "8px" }}>
                              {coordinator_name}
                            </div>
                          </div>
                        </div>

                        {stageData?.coordinator_reviewed_at && (
                          <div
                            style={{
                              fontSize: "8px",
                              color: "#757575",
                              fontStyle: "italic",
                              float: "right",
                            }}
                          >
                            {new Date(
                              stageData.coordinator_reviewed_at
                            ).toLocaleString()}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#222222",
                          lineHeight: "1.4",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {stageData.coordinator_remarks}
                      </div>
                    </div>
                  )}

                  {/* QC Manager Remark */}
                  {stageData?.manager_remarks && (
                    <div
                      style={{
                        flex: 1,
                        padding: "6px 8px",
                        backgroundColor: "#f1f8f4",
                        border: "1px solid #4caf50",
                        borderLeft: "3px solid #4caf50",
                        borderRadius: "3px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "4px",
                          }}
                        >
                          <div style={{ margin: "6px" }}>
                            <PersonOutlined sx={{ fontSize: 14 }} />
                          </div>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: "bold",
                              color: "#388e3c",
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                            }}
                          >
                            <div>QC Manager</div>
                            <div style={{ color: "black", fontSize: "8px" }}>
                              {manager_name}
                            </div>
                          </div>
                        </div>

                        {stageData?.manager_reviewed_at && (
                          <div
                            style={{
                              fontSize: "8px",
                              color: "#757575",
                              fontStyle: "italic",
                              float: "right",
                            }}
                          >
                            {new Date(
                              stageData.manager_reviewed_at
                            ).toLocaleString()}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#222222",
                          lineHeight: "1.4",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {stageData.manager_remarks}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Additional Content Section - Stage-specific content */}
          <div style={{ marginTop: "10px" }}>{children}</div>
        </div>
      </div>
    </div>
  );
};

export default ReportPdfTemplate;
